import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import useApi from '../api';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Settings, Plus, X, Info } from 'lucide-react';

interface WebsiteSettingsProps {
    open: boolean;
    websiteId: string;
    onClose: () => void;
    onSuccess?: () => void;
}

interface WebsiteSettingsData {
    autotrain: boolean;
    routeMode: 'include' | 'exclude';
    routes: string[];
}

export default function WebsiteSettings({ 
    open, 
    websiteId, 
    onClose, 
    onSuccess 
}: WebsiteSettingsProps) {
    const { get, post } = useApi();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newRoute, setNewRoute] = useState('');
    const [settings, setSettings] = useState<WebsiteSettingsData>({
        autotrain: false,
        routeMode: 'include',
        routes: []
    });

    // Fetch settings when dialog opens
    useEffect(() => {
        if (open && websiteId) {
            fetchSettings();
        }
    }, [open, websiteId]);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await get(`/websites/${websiteId}/settings`);
            setSettings({
                autotrain: response.data.autotrain || false,
                routeMode: response.data.routeMode || 'include',
                routes: response.data.routes || []
            });
        } catch (error) {
            console.error('Error fetching website settings:', error);
            toast.error('Failed to load website settings', toastConfig);
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        try {
            setSaving(true);
            await post(`/websites/${websiteId}/settings`, settings);
            toast.success('Website settings saved successfully', toastConfig);
            onSuccess?.();
            onClose();
        } catch (error: any) {
            console.error('Error saving website settings:', error);
            toast.error(error.detail || 'Failed to save website settings', toastConfig);
        } finally {
            setSaving(false);
        }
    };

    const addRoute = () => {
        if (newRoute.trim() && !settings.routes.includes(newRoute.trim())) {
            setSettings(prev => ({
                ...prev,
                routes: [...prev.routes, newRoute.trim()]
            }));
            setNewRoute('');
        }
    };

    const removeRoute = (routeToRemove: string) => {
        setSettings(prev => ({
            ...prev,
            routes: prev.routes.filter(route => route !== routeToRemove)
        }));
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addRoute();
        }
    };

    const handleClose = () => {
        setSettings({
            autotrain: false,
            routeMode: 'include',
            routes: []
        });
        setNewRoute('');
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <Settings className="w-5 h-5 mr-2" />
                        Website Settings
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin h-6 w-6 border-2 border-slate-500 rounded-full border-t-transparent"></div>
                    </div>
                ) : (
                    <div className="space-y-6 py-4">
                        {/* Autotrain Info Alert */}
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                <strong>Note:</strong> Autotrain requires your website to have a sitemap configured (e.g., sitemap.xml). 
                                If no sitemap is found, autotrain will be skipped for this website.
                            </AlertDescription>
                        </Alert>

                        {/* Autotrain Setting */}
                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label className="text-sm font-medium">Autotrain Website</Label>
                                <p className="text-sm text-muted-foreground">
                                    Automatically train new pages as they are discovered
                                </p>
                            </div>
                            <Switch
                                checked={settings.autotrain}
                                onCheckedChange={(checked) => 
                                    setSettings(prev => ({ ...prev, autotrain: checked }))
                                }
                            />
                        </div>

                        {/* Route Mode Selection */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Route Filtering</Label>
                            <RadioGroup
                                value={settings.routeMode}
                                onValueChange={(value: 'include' | 'exclude') => 
                                    setSettings(prev => ({ ...prev, routeMode: value, routes: [] }))
                                }
                            >
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="include" id="include" />
                                    <Label htmlFor="include" className="text-sm">
                                        Include only these routes
                                    </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="exclude" id="exclude" />
                                    <Label htmlFor="exclude" className="text-sm">
                                        Exclude these routes
                                    </Label>
                                </div>
                            </RadioGroup>
                        </div>

                        {/* Route Management */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">
                                {settings.routeMode === 'include' ? 'Included Routes' : 'Excluded Routes'}
                            </Label>
                            
                            {/* Add Route Input */}
                            <div className="flex space-x-2">
                                <Input
                                    placeholder={settings.routeMode === 'include' ? 
                                        "e.g., /blog, /products" : 
                                        "e.g., /admin, /api"
                                    }
                                    value={newRoute}
                                    onChange={(e) => setNewRoute(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addRoute}
                                    disabled={!newRoute.trim()}
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>

                            {/* Routes List */}
                            {settings.routes.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {settings.routes.map((route, index) => (
                                        <Badge key={index} variant="secondary" className="px-2 py-1">
                                            {route}
                                            <button
                                                onClick={() => removeRoute(route)}
                                                className="ml-2 hover:text-red-500"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            )}

                            <p className="text-xs text-muted-foreground">
                                {settings.routeMode === 'include' ? 
                                    'Only pages matching these routes will be trained' : 
                                    'Pages matching these routes will be skipped during training'
                                }
                            </p>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={saveSettings} 
                        disabled={saving || loading}
                    >
                        {saving ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                                Saving...
                            </>
                        ) : (
                            'Save Settings'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
} 